import { Dismiss24Regular } from "@fluentui/react-icons";
import { useStyle } from "./style";
import { FilePreviewProps } from "./types";
import {
	Text,
	Button,
	Dialog,
	DialogBody,
	DialogContent,
	DialogSurface,
	DialogTitle,
	DialogTrigger,
	DialogOpenChangeData,
} from "@fluentui/react-components";
import { Flex } from "@/components/atoms/flex";
import { useQuery } from "@tanstack/react-query";
import { AttachmentsKey } from "@/repository/queryFns";
import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";

enum ActionType {
	Preview,
	Download,
}

export const FilePreview = (props: FilePreviewProps) => {
	const { queryKey, queryFn, name, children } = props;
	const intl = useIntl();
	const [actionType, setActionType] = useState<ActionType>();
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const classes = useStyle();
	const { isLoading, data } = useQuery({
		queryFn: queryFn,
		queryKey: [AttachmentsKey, queryKey],
		enabled: actionType !== undefined,
	});

	useEffect(() => {
		if (!data || actionType !== ActionType.Download) {
			return;
		}

		const link = document.createElement("a");
		link.href = data;
		link.download = name || "unnamed";
		link.click();
		setActionType(undefined);
	}, [data, actionType]);

	const previewCallback = useCallback(() => {
		setActionType(ActionType.Preview);
		setIsOpen(true);
	}, [setActionType]);

	const downloadCallback = useCallback(
		() => setActionType(ActionType.Download),
		[setActionType]
	);

	return (
		<Dialog
			open={isOpen}
			onOpenChange={(_e: any, changeData: DialogOpenChangeData) =>
				setIsOpen(changeData.open)
			}
		>
			<DialogTrigger disableButtonEnhancement>
				{children(previewCallback, downloadCallback)}
			</DialogTrigger>
			<DialogSurface>
				<DialogBody>
					<DialogTitle
						action={
							<DialogTrigger
								action="close"
								children={
									<Button
										appearance="subtle"
										aria-label="close"
										icon={<Dismiss24Regular />}
									/>
								}
							/>
						}
					>
						<Text size={500} className={classes.header} children={name} />
					</DialogTitle>
					<DialogContent
						className={classes.container}
						children={
							!isLoading && !!data ? (
								<img className={classes.image} src={data} />
							) : (
								<Flex
									hAlign="center"
									vAlign="center"
									className={classes.loadingContainer}
									children={
										<Text
											children={intl.formatMessage({ id: "Common.Loading" })}
										/>
									}
								/>
							)
						}
					/>
				</DialogBody>
			</DialogSurface>
		</Dialog>
	);
};
